import React from 'react';
import type { GestureResponderEvent } from 'react-native';
import { XStack } from 'tamagui';

import { hitSlop } from '../componentHelpers';
import { Checkbox, type CheckboxProps } from '../inputs/Checkbox';
import { type BaseRowProps, Row } from './Row';

export interface CheckboxRowProps extends BaseRowProps {
    checkboxProps: Omit<CheckboxProps, 'id'>;
}

/**
 * Row component with a checkbox control.
 * Use this for selectable list items with checkbox interaction.
 * Supports click events on both the row an the checkbox for different actions.
 */
export const CheckboxRow = ({
    onPress,
    checkboxProps,
    ...rowProps
}: CheckboxRowProps) => {
    const isDisabled = checkboxProps.disabled === true;

    const handleCheckboxPress = (e: GestureResponderEvent) => {
        if (isDisabled) {
            return;
        }
        e.stopPropagation(); // do not trigger row press.
        checkboxProps.onPress?.(e);
    };

    return (
        <Row
            {...rowProps}
            onPress={onPress} // row press
            rightContent={
                <XStack
                    onPress={isDisabled ? undefined : handleCheckboxPress} // checkbox press
                    {...hitSlop(8)}
                    paddingVertical={8} // to match hitSlop
                    paddingLeft={8} // to match hitSlop, omit paddingRight to ensure right alignment.
                    pointerEvents={isDisabled ? 'none' : 'box-only'}
                >
                    <Checkbox
                        id={`checkbox-row-${rowProps.title}`}
                        {...checkboxProps}
                    />
                </XStack>
            }
        />
    );
};
